import React, { useState, useEffect } from 'react';
import '../../styles/components/FormPage.css';
import { Input as FormInput } from '../FormField';
import TagMultiSelect from '../ui/TagMultiSelect';
import SearchableSelect from '../ui/SearchableSelect';
import LineItemsEditor, { LineItem, LineItemFieldConfig } from '../ui/LineItemsEditor';
import {
  Guideline, GuidelineCategory, GuidelineItem,
  GUIDELINE_CATEGORY_LABELS, GUIDELINE_CATEGORIES,
  getGuidelineById, createGuideline, updateGuidelineStrict, archiveGuideline,
} from '../../services/GuidelinesService';
import { loadVendorData } from '../../data/VendorLoader';
import { generateId, useFormSaveEvent, useFormDeleteEvent, firstMissingField, useClearErrorsOnEdit } from './_shared';
import { useNotifications } from '../NotificationProvider';
import { GUIDELINE_TEAM_OPTIONS } from './constants';

interface GuidelineFormPageProps {
  guidelineId?: string;
  onSave: () => void;
  onCancel: () => void;
}

export const GuidelineFormPage: React.FC<GuidelineFormPageProps> = ({ guidelineId, onSave, onCancel }) => {
  const { showError, showSuccess } = useNotifications();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<GuidelineCategory>('general');
  const [teamTags, setTeamTags] = useState<string[]>([]);
  const [vendorNames, setVendorNames] = useState<string[]>([]);
  const [items, setItems] = useState<GuidelineItem[]>([{ id: generateId(), text: '', emphasis: false }]);

  useEffect(() => {
    loadVendorData().then(vendors => {
      setVendorNames(vendors.map(v => v.name).filter(Boolean).sort());
    });
  }, []);

  useEffect(() => {
    if (!guidelineId) return;
    const existing = getGuidelineById(guidelineId);
    if (existing) {
      setTitle(existing.title);
      setCategory(existing.category);
      setTeamTags(existing.team_tag ? existing.team_tag.split(',').map(t => t.trim()).filter(Boolean) : []);
      setItems(existing.items.length > 0 ? existing.items : [{ id: generateId(), text: '', emphasis: false }]);
    }
  }, [guidelineId]);

  const lineItems: LineItem[] = items.map(i => ({ id: i.id, values: { text: i.text, emphasis: i.emphasis ? 'true' : 'false' } }));
  const handleLineItemsChange = (next: LineItem[]) =>
    setItems(next.map(li => ({ id: li.id, text: li.values.text ?? '', emphasis: li.values.emphasis === 'true' })));
  const ruleFields: LineItemFieldConfig[] = [
    { key: 'text', type: 'text', placeholder: 'Enter a rule or guideline…' },
    { key: 'emphasis', type: 'select', width: 120, options: [
      { value: 'false', label: 'Normal' },
      { value: 'true', label: 'Important' },
    ] },
  ];

  const handleSave = async () => {
    const missing = firstMissingField([{ label: 'Title', value: title }]);
    if (missing) { showError(missing); return; }
    const validItems = items.filter(i => i.text.trim());
    if (validItems.length === 0) { showError('Add at least one rule or item'); return; }
    const data = { title: title.trim(), category, items: validItems, team_tag: teamTags.length > 0 ? teamTags.join(', ') : undefined };
    // Both calls wait for the API and throw on failure, so a 422 reaches the
    // shared hook (field errors under their fields) and the form stays open.
    if (guidelineId) { await updateGuidelineStrict(guidelineId, data); } else { await createGuideline(data, { strict: true }); }
    onSave();
  };

  const fieldErrors = useFormSaveEvent(handleSave, { fallback: 'Failed to save the guideline.' });
  useClearErrorsOnEdit({ title, category, team_tag: teamTags, items }, fieldErrors);

  const handleDelete = () => {
    if (!guidelineId) return;
    archiveGuideline(guidelineId);
    showSuccess('Guideline archived');
    onCancel();
  };

  useFormDeleteEvent(handleDelete);

  return (
    <div className="inline-form-page">
      <div className="form-row">
        <FormInput label="Title" type="text" value={title}
          error={fieldErrors.errorFor('title')}
          onChange={e => setTitle(e.target.value)} placeholder="e.g. Band Guidelines" autoFocus required />
        <div className="form-group">
          <label className="form-label">Category</label>
          <SearchableSelect value={category} error={fieldErrors.errorFor('category')}
            onChange={value => setCategory(value as GuidelineCategory)}
            options={GUIDELINE_CATEGORIES.map(c => ({ value: c, label: GUIDELINE_CATEGORY_LABELS[c] }))} />
        </div>
      </div>
      <div className="form-group">
        <label>Vendor / Team</label>
        <TagMultiSelect
          values={teamTags}
          onChange={setTeamTags}
          groups={[
            { label: 'Vendors', options: vendorNames },
            { label: 'Teams', options: GUIDELINE_TEAM_OPTIONS },
          ]}
          placeholder="Select vendors or teams this applies to…"
          allowCreate={false}
        />
        {fieldErrors.errorFor('team_tag') && <span className="form-error-text">{fieldErrors.errorFor('team_tag')}</span>}
        <span className="sfp-hint-sm">This guideline will appear in each selected vendor's brief.</span>
      </div>
      <div className="form-group">
        <label>Rules / Items <span className="form-required">*</span></label>
        <LineItemsEditor
          items={lineItems}
          fields={ruleFields}
          onChange={handleLineItemsChange}
          addLabel="Add another rule"
          wideGrower
          genId={generateId}
        />
        {fieldErrors.errorFor('items') && <span className="form-error-text">{fieldErrors.errorFor('items')}</span>}
      </div>
    </div>
  );
};
